import api from './api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface Ticket {
  id: string;
  codigo: string;
  titulo: string;
  descricao: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO';
  clientId: string;
  createdAt: string;
  updatedAt: string;
  // Campos para Catraca de Aprovação
  horasEstimadas?: number | null;
  valorEstimado?: number | null;
  aprovado: boolean;
  aprovadoEm?: string | null;
  aprovadoPorId?: string | null;
  aprovadoPorTipo?: string | null;
  emergencial?: boolean;
  dataPrazo?: string | null;
  escopoFaturado?: boolean;
  apontamentos?: Array<any>;
  client: {
    id: string;
    razaoSocial: string;
    nomeFantasia: string | null;
  };
  _count?: {
    apontamentos: number;
  };
}

export interface TicketCreateData {
  clientId: string;
  titulo: string;
  descricao: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status?: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO';
  // Campos para Catraca de Aprovação
  horasEstimadas?: number;
  valorEstimado?: number;
  // Situação emergencial: auto-aprovado, sem catraca
  emergencial?: boolean;
  dataPrazo?: string; // prazo do atendimento (Upcoming Deadlines)
}

export interface TicketUpdateData {
  titulo?: string;
  descricao?: string;
  prioridade?: 'BAIXA' | 'MEDIA' | 'ALTA';
  status?: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO';
  // Campos para Catraca de Aprovação
  horasEstimadas?: number;
  valorEstimado?: number;
  aprovado?: boolean;
  aprovadoPorId?: string;
  dataPrazo?: string | null;
}

export interface TicketFilters {
  clientId?: string;
  prioridade?: 'BAIXA' | 'MEDIA' | 'ALTA';
  status?: string;
  dataInicio?: string;
  dataFim?: string;
}

// Máquina de estados dos tickets (espelha a regra do backend em ticketService.ts).
// `AGUARDANDO_APROVACAO` só sai via POST /:id/aprovar; `FECHADO` é terminal.
export const TRANSICOES_TICKET: Record<string, string[]> = {
  ABERTO: ['EM_ANDAMENTO', 'AGUARDANDO_CLIENTE', 'AGUARDANDO_APROVACAO', 'FECHADO'],
  EM_ANDAMENTO: ['AGUARDANDO_CLIENTE', 'AGUARDANDO_APROVACAO', 'RESOLVIDO'],
  AGUARDANDO_CLIENTE: ['EM_ANDAMENTO', 'RESOLVIDO', 'FECHADO'],
  AGUARDANDO_APROVACAO: [],
  RESOLVIDO: ['EM_ANDAMENTO', 'FECHADO'],
  FECHADO: [],
};

export const STATUS_TICKET_LABELS: Record<string, string> = {
  ABERTO: 'Aberto',
  EM_ANDAMENTO: 'Em Andamento',
  AGUARDANDO_CLIENTE: 'Aguardando Cliente',
  AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
  RESOLVIDO: 'Resolvido',
  FECHADO: 'Fechado',
};

// Rótulos amigáveis para as ações de transição de status (fluxo guiado)
export const STATUS_ACTION_LABELS: Record<string, string> = {
  EM_ANDAMENTO: 'Iniciar Atendimento',
  AGUARDANDO_CLIENTE: 'Solicitar Informações',
  AGUARDANDO_APROVACAO: 'Enviar para Aprovação',
  RESOLVIDO: 'Concluir',
  FECHADO: 'Arquivar',
  ABERTO: 'Reabrir',
};

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Cria um novo ticket
 */
export const createTicket = async (data: TicketCreateData): Promise<Ticket> => {
  const response = await api.post('/tickets', data);
  return response.data;
};

/**
 * Lista tickets com filtros opcionais
 */
export const listTickets = async (filters?: TicketFilters): Promise<Ticket[]> => {
  const params = new URLSearchParams();
  
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.prioridade) params.append('prioridade', filters.prioridade);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio);
  if (filters?.dataFim) params.append('dataFim', filters.dataFim);

  const response = await api.get(`/tickets?${params.toString()}`);
  return response.data;
};

/**
 * Busca ticket por ID
 */
export const getTicketById = async (id: string): Promise<Ticket> => {
  const response = await api.get(`/tickets/${id}`);
  return response.data;
};

/**
 * Atualiza um ticket
 */
export const updateTicket = async (id: string, data: TicketUpdateData): Promise<Ticket> => {
  const response = await api.put(`/tickets/${id}`, data);
  return response.data;
};

/**
 * Remove um ticket
 */
export const deleteTicket = async (id: string): Promise<void> => {
  await api.delete(`/tickets/${id}`);
};

/**
 * Faz download do relatório técnico em PDF
 */
export const downloadRelatorio = async (id: string): Promise<void> => {
  const response = await api.get(`/tickets/${id}/relatorio`, {
    responseType: 'blob',
  });

  // Extrair nome do arquivo do header Content-Disposition
  const contentDisposition = response.headers['content-disposition'];
  let filename = `relatorio-ticket-${id}.pdf`;
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Criar link de download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Publica o relatório técnico no Portal do Cliente
 */
export const publicarRelatorio = async (id: string): Promise<{ message: string; relatorio: any }> => {
  const response = await api.post(`/tickets/${id}/publicar-relatorio`);
  return response.data;
};

/**
 * Aprova um ticket (Fluxo de Catraca de Aprovação)
 */
export const aprovarTicket = async (id: string): Promise<Ticket> => {
  const response = await api.post(`/tickets/${id}/aprovar`);
  return response.data.ticket;
};

/**
 * Rejeita a aprovação de um ticket (cliente "não aprova / pede ajuste")
 */
export const rejeitarTicket = async (id: string, motivo: string): Promise<Ticket> => {
  const response = await api.post(`/tickets/${id}/rejeitar`, { motivo });
  return response.data.ticket;
};

/**
 * Solicita informações ao cliente (registra mensagem na timeline e move para AGUARDANDO_CLIENTE)
 */
export const solicitarInformacoes = async (id: string, mensagem: string): Promise<any> => {
  const response = await api.post(`/tickets/${id}/solicitar-informacoes`, { mensagem });
  return response.data;
};

// ─── Export do Service como Objeto ────────────────────────────────────────────

export const ticketService = {
  create: createTicket,
  list: listTickets,
  getById: getTicketById,
  update: updateTicket,
  delete: deleteTicket,
  downloadRelatorio,
  publicarRelatorio,
  aprovar: aprovarTicket,
  rejeitar: rejeitarTicket,
  solicitarInformacoes,
};
